import React from "react";
import { PostJobCheckList } from "./types";
import { PostJobCheckListForm } from "./PostJobCheckListForm";
import { useMutation, useQuery } from "@apollo/client";
import {
  Mutation,
  MutationUpdateInvoiceArgs,
  MutationUpdateJobArgs,
  Query,
  QueryGetInvoiceArgs,
  UpdateInvoiceInput,
  UpdateJobInput,
} from "../../generated/nest-graphql";
import { UPDATE_JOB } from "../../graphql/mutations/updateJob";
import { UPDATE_INVOICE } from "../../graphql/mutations/updateInvoice";
import { GET_INVOICE } from "../../graphql/queries/getInvoice";
import { flow } from "lodash";
import { postJobCheckListUpdateJobSpec } from "../Jobs/jobDetailsSpec";
import { formValuesToEJIServiceInputsSpec } from "../../specs/ejiServicesSpec";
import { ejiServicesToFormValuesSpec } from "../../specs/ejiServicesSpec";
import { keys } from "ramda";
import { showSuccessAlert } from "../../actions";
import { useDispatch } from "../../contexts/snackbar-context";
import { useInspectionReport } from "../../contexts/inspection-context";
import { PostJobCheckListFormCore } from "./PostJobCheckListFormCore";

export type PostJobCheckListFormValues = PostJobCheckList;

export const PostJobCheckListDialog = ({
  jobId,
  open,
  onClose,
  initialValues,
  parentSubmit,
  proceedingWithRepairs,
  isVioc,
  invoiceId,
}: {
  jobId: string;
  open: boolean;
  onClose: () => void;
  initialValues: PostJobCheckListFormValues;
  parentSubmit: () => void;
  proceedingWithRepairs?: boolean;
  isVioc?: boolean;
  invoiceId?: string;
}) => {
  const dispatch = useDispatch();
  const [updateJob] = useMutation<Mutation, MutationUpdateJobArgs>(UPDATE_JOB);
  const [updateInvoice] = useMutation<Mutation, MutationUpdateInvoiceArgs>(UPDATE_INVOICE);
  const { state } = useInspectionReport();
  const { data: invoiceData } = useQuery<Query, QueryGetInvoiceArgs>(GET_INVOICE, {
    variables: { id: invoiceId || "" },
    skip: !invoiceId,
    fetchPolicy: "cache-first",
  });

  const currentInvoice = invoiceData?.getInvoice;

  const onSubmit = async (values: PostJobCheckListFormValues) => {
    const input: UpdateJobInput = flow(postJobCheckListUpdateJobSpec)(
      values as PostJobCheckListFormValues,
      proceedingWithRepairs
    );
    await updateJob({
      variables: {
        id: jobId,
        updateJobInput: input,
      },
    });

    if (invoiceId && values.partItems && Object.keys(values.partItems).length > 0 && currentInvoice) {
      const servicesFormValues = ejiServicesToFormValuesSpec(currentInvoice.services || []);

      const updatedServices = servicesFormValues.map((service) => ({
        ...service,
        items: service.items.map((item) => {
          const partItemData = item.id ? values.partItems[item.id] : null;
          if (partItemData) {
            return {
              ...item,
              warranty_replacement_reason: partItemData.warranty_replacement_reason || null,
              warranty_replacement_upgrade: partItemData.warranty_replacement_upgrade || false,
            };
          }
          return item;
        }),
      }));

      const invoiceInput: UpdateInvoiceInput = {
        services: formValuesToEJIServiceInputsSpec(updatedServices),
      };

      await updateInvoice({
        variables: {
          id: invoiceId,
          updateInvoiceInput: invoiceInput,
        },
      });
    }

    parentSubmit();
    onClose();
    showSuccessAlert(dispatch, "Success");
  };

  if (!isVioc) {
    return (
      <PostJobCheckListFormCore
        key={"pre-job-2"}
        onSubmit={onSubmit}
        open={open}
        onClose={onClose}
        initialValues={initialValues}
        jobId={jobId}
        invoiceId={invoiceId}
      />
    );
  }

  return (
    <PostJobCheckListForm
      key={"pre-job-2"}
      onSubmit={onSubmit}
      open={open}
      onClose={onClose}
      jobId={jobId}
      initialValues={initialValues}
      proceedingWithRepairs={proceedingWithRepairs}
      invoiceId={invoiceId}
    />
  );
};
